import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {useCallback, useEffect, useState} from 'react';
import {useAppSelector} from 'src/redux/hooks';
import {selectProperty} from 'src/redux/slice/propertySlice';
import {
  getLastAmenitiesPropertyId,
  setLastAmenitiesPropertyId,
} from 'src/utils/contextPreferences';
import AddAmenityModal from 'src/components/Modals/AddAmenity';
import SearchApartments from 'src/components/SearchApartment';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {Amenity} from 'src/apis/autoGenerate/types/amenity';
import {
  amenityServiceDelete,
  amenityServiceDetail,
  amenityServiceFind,
} from 'src/apis/autoGenerate/services';
import WeeklyScheduleCalendar from 'src/components/WeeklyScheduleCalendar';
import AmenityReservationsPanel from 'src/pages/Amenity/AmenityReservationsPanel';
import AmenityLinkedDevicesPanel from 'src/pages/Amenity/AmenityLinkedDevicesPanel';
import message from 'src/components/Message';
import Spin from 'src/components/Spin';
import {useOrgId, useCanEdit} from 'src/hooks';
import {PageHeader, EmptyState} from 'src/components/ui';

export default function AmenityList() {
  const navigate = useNavigate();
  const property = useAppSelector(selectProperty);
  const [searchApartment, setSearchApartment] = useState<Apartment | null>(null);
  const [propertyPickerOpen, setPropertyPickerOpen] = useState(false);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedAmenityId, setSelectedAmenityId] = useState('');
  const [addAmenityOpen, setAddAmenityOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | undefined>();
  const [amenityDetail, setAmenityDetail] = useState<Amenity | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState<'schedule' | 'reservations' | 'linkedDevices'>(
    'schedule'
  );
  const {orgId} = useOrgId();
  const canEdit = useCanEdit();

  useEffect(() => {
    if (!orgId || property.loadingList) {
      return;
    }
    const list = property.apartmentList;
    setSearchApartment(prev => {
      if (prev && list.some(a => a.id === prev.id)) {
        return prev;
      }
      const storedId = getLastAmenitiesPropertyId(orgId);
      if (storedId) {
        const found = list.find(a => a.id === storedId);
        if (found) {
          return found;
        }
      }
      if (list.length === 1) {
        return list[0];
      }
      return null;
    });
  }, [orgId, property.apartmentList, property.loadingList]);

  const handlePropertyChange = useCallback(
    (apartment: Apartment | null) => {
      setSearchApartment(apartment);
      setPropertyPickerOpen(false);
      if (orgId && apartment === null) {
        setLastAmenitiesPropertyId(orgId, null);
      }
    },
    [orgId]
  );

  useEffect(() => {
    if (!orgId || !searchApartment?.id) {
      return;
    }
    setLastAmenitiesPropertyId(orgId, searchApartment.id);
  }, [orgId, searchApartment?.id]);

  const fetchAmenities = useCallback(async () => {
    if (!orgId || !searchApartment?.id) {
      setAmenities([]);
      setSelectedAmenityId('');
      return;
    }
    setLoadingList(true);
    try {
      const res = await amenityServiceFind(orgId, searchApartment.id);
      const data = res?.data as {amenities?: Amenity[]};
      const list = data?.amenities || [];
      setAmenities(list);
      setSelectedAmenityId(prev => {
        if (prev && list.some(a => a.id === prev)) return prev;
        return list[0]?.id || '';
      });
    } catch {
      message.error('Failed to load amenities');
      setAmenities([]);
      setSelectedAmenityId('');
    } finally {
      setLoadingList(false);
    }
  }, [orgId, searchApartment?.id]);

  useEffect(() => {
    fetchAmenities();
  }, [fetchAmenities]);

  const fetchAmenityDetail = useCallback(async () => {
    if (!selectedAmenityId) {
      setAmenityDetail(null);
      return;
    }
    setLoadingDetail(true);
    try {
      const res = await amenityServiceDetail(selectedAmenityId);
      const data = (res as {data?: Amenity})?.data;
      setAmenityDetail(data || null);
    } catch {
      setAmenityDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedAmenityId]);

  useEffect(() => {
    if (!selectedAmenityId) {
      setAmenityDetail(null);
      return;
    }
    fetchAmenityDetail();
  }, [selectedAmenityId, amenities, fetchAmenityDetail]);

  useEffect(() => {
    setDetailTab('schedule');
  }, [selectedAmenityId]);

  const handleDeleteAmenity = (id: string) => {
    amenityServiceDelete(id)
      .then(() => {
        message.success('Amenity deleted');
        fetchAmenities();
        if (selectedAmenityId === id) {
          setSelectedAmenityId('');
        }
      })
      .catch(() => {
        message.error('Failed to delete amenity');
      });
  };

  const hasProperty = Boolean(searchApartment?.id);
  const showMainGrid = hasProperty && amenities.length > 0;
  const emptyPropertyAmenities = hasProperty && !loadingList && amenities.length === 0;
  const apartmentList = property.apartmentList;
  const noPropertiesInOrg = !property.loadingList && apartmentList.length === 0;
  const needsPropertyChoice = !property.loadingList && apartmentList.length > 0 && !hasProperty;

  return (
    <Box sx={{px: 3, py: 2, height: '100%', display: 'flex', flexDirection: 'column'}}>
      <Spin spinning={property.loadingList || loadingList || loadingDetail} />
      <PageHeader title="Amenities" />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}} flexWrap="wrap" gap={2}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <SearchApartments
            value={searchApartment}
            onChange={handlePropertyChange}
            placeholder="Select property"
            open={propertyPickerOpen}
            onOpen={() => setPropertyPickerOpen(true)}
            onClose={() => setPropertyPickerOpen(false)}
          />
          {searchApartment?.name && (
            <Typography variant="subtitle1" sx={{fontWeight: 600, color: 'text.primary'}}>
              {searchApartment.name}
            </Typography>
          )}
        </Stack>
        {canEdit && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            disabled={!hasProperty}
            onClick={() => {
              setEditingAmenity(undefined);
              setAddAmenityOpen(true);
            }}
          >
            Add amenity
          </Button>
        )}
      </Stack>

      {noPropertiesInOrg && (
        <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320}}>
          <EmptyState
            title="No properties yet"
            description="Add a property first to manage amenities."
            action={{label: 'Create property', onClick: () => navigate('/property')}}
          />
        </Box>
      )}

      {needsPropertyChoice && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
          }}
        >
          <EmptyState
            title="No property selected"
            description="Choose a property to manage amenities, schedules, and reservations for that community."
            action={{label: 'Select property', onClick: () => setPropertyPickerOpen(true)}}
          />
          {canEdit && (
            <Button variant="outlined" component={RouterLink} to="/property" sx={{mt: 1}}>
              Create property
            </Button>
          )}
        </Box>
      )}

      {emptyPropertyAmenities && (
        <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 280}}>
          <EmptyState
            title="No amenities yet"
            description="Create amenities for your residents to discover and reserve."
            action={
              canEdit
                ? {
                    label: 'Add Amenity',
                    onClick: () => {
                      setEditingAmenity(undefined);
                      setAddAmenityOpen(true);
                    },
                    icon: <AddIcon />,
                  }
                : undefined
            }
          />
        </Box>
      )}

      {showMainGrid && (
        <Grid container sx={{flex: 1, minHeight: 0, pt: 1}}>
          <Grid item xs={12} md={3}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1}}>
              <Typography variant="subtitle2" color="text.secondary">
                Amenities
              </Typography>
              {canEdit && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingAmenity(undefined);
                    setAddAmenityOpen(true);
                  }}
                  aria-label="Add amenity"
                >
                  <AddIcon />
                </IconButton>
              )}
            </Box>
            <List
              component="nav"
              aria-label="amenity list"
              sx={{height: 'calc(100vh - 220px)', overflow: 'auto', pr: 1}}
            >
              {amenities.map(item => {
                const id = item.id as string;
                const isSelected = selectedAmenityId === id;
                return (
                  <ListItemButton
                    key={id}
                    selected={isSelected}
                    onClick={() => setSelectedAmenityId(id)}
                    sx={{'&:hover': {'& .amenity-actions': {opacity: 1}}}}
                  >
                    <ListItemText
                      primary={item.name}
                      sx={{
                        color: theme =>
                          isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                      }}
                    />
                    {canEdit && (
                      <>
                        <IconButton
                          className="amenity-actions"
                          sx={{opacity: 0}}
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            setEditingAmenity(item);
                            setAddAmenityOpen(true);
                          }}
                          aria-label="Edit amenity"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          className="amenity-actions"
                          sx={{opacity: 0}}
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteAmenity(id);
                          }}
                          aria-label="Delete amenity"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Grid>
          <Grid
            item
            xs={12}
            md={9}
            sx={{display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0}}
          >
            {selectedAmenityId && amenityDetail ? (
              <Box sx={{px: {xs: 0, md: 2}, display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                  <Typography variant="h6">{amenityDetail.name}</Typography>
                  {canEdit && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setEditingAmenity(amenityDetail);
                        setAddAmenityOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Stack>

                <Tabs
                  value={detailTab}
                  onChange={(_, v) => setDetailTab(v)}
                  sx={{mb: 2, borderBottom: 1, borderColor: 'divider'}}
                  aria-label={`${amenityDetail.name} views`}
                >
                  <Tab label="Schedule" value="schedule" />
                  <Tab label="Reservations" value="reservations" />
                  <Tab label="Linked Device" value="linkedDevices" />
                </Tabs>

                {detailTab === 'schedule' && (
                  <>
                    <Box sx={{mb: 3}}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                        Configuration
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Capacity
                          </Typography>
                          <Typography variant="body2">{amenityDetail.capacity ?? 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Open Hours
                          </Typography>
                          <Typography variant="body2">
                            {amenityDetail.openHours
                              ? (amenityDetail.openHours.start === '00:00' && amenityDetail.openHours.end === '00:00'
                                ? 'Full Day'
                                : `${amenityDetail.openHours.start} - ${amenityDetail.openHours.end}`)
                              : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Slot Duration
                          </Typography>
                          <Typography variant="body2">
                            {amenityDetail.durationOptions?.[0]
                              ? amenityDetail.durationOptions[0] >= 60
                                ? `${amenityDetail.durationOptions[0] / 60}h`
                                : `${amenityDetail.durationOptions[0]}m`
                              : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Max Bookings/User/Day
                          </Typography>
                          <Typography variant="body2">{amenityDetail.maxBookingsPerUserPerDay ?? 1}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Advance Booking Days
                          </Typography>
                          <Typography variant="body2">{amenityDetail.maxBookingDays ?? 7} days</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Divider sx={{my: 2}} />

                    {amenityDetail.openHours ? (
                      <WeeklyScheduleCalendar
                        amenityId={selectedAmenityId}
                        openHours={amenityDetail.openHours}
                        duration={amenityDetail.durationOptions?.[0] || 60}
                        capacity={amenityDetail.capacity || 1}
                        apartmentTimeZone={searchApartment?.timeZone}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Set open hours in Edit to see the weekly schedule.
                      </Typography>
                    )}
                  </>
                )}

                {detailTab === 'reservations' && (
                  <AmenityReservationsPanel
                    amenityId={selectedAmenityId}
                    apartmentTimeZone={searchApartment?.timeZone}
                  />
                )}

                {searchApartment?.id && (
                  <AmenityLinkedDevicesPanel
                    active={detailTab === 'linkedDevices'}
                    orgId={orgId}
                    apartmentId={searchApartment.id}
                    amenityDetail={amenityDetail}
                    refetchAmenityDetail={fetchAmenityDetail}
                  />
                )}
              </Box>
            ) : selectedAmenityId ? (
              <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200}}>
                <EmptyState compact title="Loading amenity details..." />
              </Box>
            ) : (
              <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200}}>
                <EmptyState
                  compact
                  title="Select an amenity"
                  description="Choose an amenity from the list to view details and manage the schedule."
                />
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      {addAmenityOpen && (
        <AddAmenityModal
          open={addAmenityOpen}
          amenity={editingAmenity}
          defaultApartment={searchApartment}
          setOpen={setAddAmenityOpen}
          onClose={() => {
            setAddAmenityOpen(false);
            setEditingAmenity(undefined);
          }}
          handleConfirm={() => {
            setAddAmenityOpen(false);
            setEditingAmenity(undefined);
            fetchAmenities();
          }}
        />
      )}
    </Box>
  );
}
